import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

// Job status enum
export enum AsyncJobStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELED = 'canceled',
}

// Define the job completion handler type
export type AsyncJobCompletionHandler<T> = (result: T) => void;

// Define the complete job configuration
export interface AsyncJobConfig<T> {
  name: string;
  executor: () => Promise<T>;
  onSuccess?: AsyncJobCompletionHandler<T>;
  onError?: (error: Error) => void;
  metadata?: Record<string, any>;
}

// Base job type
export interface AsyncJob<T = unknown> {
  id: string;
  name: string;
  status: AsyncJobStatus;
  createdAt: Date;
  startedAt?: Date;
  completedAt?: Date;
  progress?: number; // 0-100
  result?: T;
  error?: Error | { message: string; stack?: string };
  metadata?: Record<string, any>;
}

interface AsyncJobsContextProps {
  children: ReactNode;
}

interface AsyncJobsContextValue {
  jobs: AsyncJob[];
  startJob: <T>(jobConfig: AsyncJobConfig<T>) => AsyncJob<T>;
  updateJob: <T>(id: string, updates: Partial<AsyncJob<T>>) => void;
  cancelJob: (id: string) => void;
  getJob: <T>(id: string) => AsyncJob<T> | undefined;
  clearCompletedJobs: () => void;
  clearAllJobs: () => void;
}

const AsyncJobsContext = createContext<AsyncJobsContextValue | undefined>(
  undefined
);

export const AsyncJobsProvider = ({ children }: AsyncJobsContextProps) => {
  const [jobs, setJobs] = useState<AsyncJob[]>([]);

  // Track completion handlers for each job
  const completionHandlersRef = React.useRef<
    Map<string, (result: any) => void>
  >(new Map());
  const errorHandlersRef = React.useRef<Map<string, (error: Error) => void>>(
    new Map()
  );

  // Generate unique ID for jobs
  const generateId = useCallback(() => {
    return `job_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }, []);

  // Start a new job with a complete job configuration
  const startJob = useCallback(
    <T,>(jobConfig: AsyncJobConfig<T>): AsyncJob<T> => {
      const { name, executor, onSuccess, onError, metadata } = jobConfig;

      const newJob: AsyncJob<T> = {
        id: generateId(),
        name,
        status: AsyncJobStatus.PENDING,
        createdAt: new Date(),
        metadata,
      };

      // Add job to the list
      setJobs((prevJobs) => [...prevJobs, newJob]);

      // Store the completion handlers for later
      if (onSuccess) {
        completionHandlersRef.current.set(newJob.id, onSuccess);
      }

      if (onError) {
        errorHandlersRef.current.set(newJob.id, onError);
      }

      // Execute the job asynchronously
      setTimeout(() => {
        // Update status to running
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === newJob.id
              ? {
                  ...job,
                  status: AsyncJobStatus.RUNNING,
                  startedAt: new Date(),
                }
              : job
          )
        );

        // Execute the job
        executor()
          .then((result) => {
            // Update job with result
            setJobs((prevJobs) =>
              prevJobs.map((job) =>
                job.id === newJob.id
                  ? {
                      ...job,
                      status: AsyncJobStatus.COMPLETED,
                      completedAt: new Date(),
                      result,
                      progress: 100,
                    }
                  : job
              )
            );

            // Call the success handler if one was provided
            const successHandler = completionHandlersRef.current.get(newJob.id);
            if (successHandler) {
              successHandler(result);
              // Clean up handler after use
              completionHandlersRef.current.delete(newJob.id);
            }
          })
          .catch((error) => {
            // Update job with error
            setJobs((prevJobs) =>
              prevJobs.map((job) =>
                job.id === newJob.id
                  ? {
                      ...job,
                      status: AsyncJobStatus.FAILED,
                      completedAt: new Date(),
                      error: {
                        message: error.message,
                        stack: error.stack,
                      },
                    }
                  : job
              )
            );

            // Call the error handler if one was provided
            const errHandler = errorHandlersRef.current.get(newJob.id);
            if (errHandler) {
              errHandler(error);
              // Clean up handler after use
              errorHandlersRef.current.delete(newJob.id);
            }
          });
      }, 0);

      return newJob;
    },
    [generateId]
  );

  // Update job status, progress, result, etc.
  const updateJob = useCallback(
    <T,>(id: string, updates: Partial<AsyncJob<T>>): void => {
      setJobs((prevJobs) =>
        prevJobs.map((job) => (job.id === id ? { ...job, ...updates } : job))
      );

      // If the job is being marked as completed, call the success handler
      if (updates.status === AsyncJobStatus.COMPLETED && updates.result) {
        const successHandler = completionHandlersRef.current.get(id);
        if (successHandler) {
          successHandler(updates.result);
          // Clean up handler after use
          completionHandlersRef.current.delete(id);
        }
      }

      // If the job is being marked as failed, call the error handler
      if (updates.status === AsyncJobStatus.FAILED && updates.error) {
        const errHandler = errorHandlersRef.current.get(id);
        if (errHandler) {
          errHandler(updates.error as Error);
          // Clean up handler after use
          errorHandlersRef.current.delete(id);
        }
      }
    },
    []
  );

  // Cancel a job
  const cancelJob = useCallback((id: string) => {
    setJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.id === id &&
        (job.status === AsyncJobStatus.PENDING ||
          job.status === AsyncJobStatus.RUNNING)
          ? { ...job, status: AsyncJobStatus.CANCELED, completedAt: new Date() }
          : job
      )
    );

    // Clean up any handlers
    completionHandlersRef.current.delete(id);
    errorHandlersRef.current.delete(id);
  }, []);

  // Get a job by ID
  const getJob = useCallback(
    <T,>(id: string): AsyncJob<T> | undefined => {
      return jobs.find((job) => job.id === id) as AsyncJob<T> | undefined;
    },
    [jobs]
  );

  // Clear completed jobs
  const clearCompletedJobs = useCallback(() => {
    setJobs((prevJobs) => {
      const jobsToRemove = prevJobs.filter(
        (job) =>
          job.status === AsyncJobStatus.COMPLETED ||
          job.status === AsyncJobStatus.FAILED ||
          job.status === AsyncJobStatus.CANCELED
      );

      // Clean up any handlers for jobs being removed
      jobsToRemove.forEach((job) => {
        completionHandlersRef.current.delete(job.id);
        errorHandlersRef.current.delete(job.id);
      });

      return prevJobs.filter(
        (job) =>
          job.status !== AsyncJobStatus.COMPLETED &&
          job.status !== AsyncJobStatus.FAILED &&
          job.status !== AsyncJobStatus.CANCELED
      );
    });
  }, []);

  // Clear all jobs
  const clearAllJobs = useCallback(() => {
    setJobs([]);
    // Clean up all handlers
    completionHandlersRef.current.clear();
    errorHandlersRef.current.clear();
  }, []);

  // Cleanup old completed jobs automatically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = new Date();
      setJobs((prevJobs) => {
        const jobsToRemove = prevJobs.filter((job) => {
          if (
            (job.status === AsyncJobStatus.COMPLETED ||
              job.status === AsyncJobStatus.FAILED ||
              job.status === AsyncJobStatus.CANCELED) &&
            job.completedAt
          ) {
            const hoursSinceCompletion =
              (now.getTime() - job.completedAt.getTime()) / (1000 * 60 * 60);
            return hoursSinceCompletion >= 24;
          }
          return false;
        });

        // Clean up any handlers for jobs being removed
        jobsToRemove.forEach((job) => {
          completionHandlersRef.current.delete(job.id);
          errorHandlersRef.current.delete(job.id);
        });

        return prevJobs.filter((job) => {
          if (
            (job.status === AsyncJobStatus.COMPLETED ||
              job.status === AsyncJobStatus.FAILED ||
              job.status === AsyncJobStatus.CANCELED) &&
            job.completedAt
          ) {
            const hoursSinceCompletion =
              (now.getTime() - job.completedAt.getTime()) / (1000 * 60 * 60);
            return hoursSinceCompletion < 24;
          }
          return true;
        });
      });
    }, 60 * 60 * 1000); // Run every hour

    return () => clearInterval(cleanupInterval);
  }, []);

  return (
    <AsyncJobsContext.Provider
      value={{
        jobs,
        startJob,
        updateJob,
        cancelJob,
        getJob,
        clearCompletedJobs,
        clearAllJobs,
      }}
    >
      {children}
    </AsyncJobsContext.Provider>
  );
};

export const useAsyncJobs = () => {
  const context = useContext(AsyncJobsContext);
  if (!context) {
    throw new Error('useAsyncJobs must be used within a AsyncJobsProvider');
  }
  return context;
};
